import { Box, Button, Divider, Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { getScimTokens } from "routes/settings/plugins/scim/controller/get-scim-tokens"
import { getScimEndpoints } from "routes/settings/plugins/scim/controller/get-scim-endpoints"
import { CopyableField } from "routes/settings/plugins/sso/components/copyable-field"
import { TokenRow } from "./token-row"
import { handleCreateToken } from "./handle-create-token"
import { CircuitBreakerSection } from "./circuit-breaker-section"

export function ScimConnectionSection() {
    const { client } = useBoundContext()
    const clientId = client?.id
    const tokens = getScimTokens.useResults({ clientId }) ?? []
    const endpoints = getScimEndpoints.useResults()

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                SCIM connection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Issue a bearer token to your identity provider so it can provision and deprovision users. Each tenant
                uses one active token; create a new token to rotate, and revoke the old one.
            </Typography>

            <If value={!!endpoints?.baseUrl}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Provisioning endpoint
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Paste the base URL into your identity provider's SCIM connector configuration. Most IdPs (Okta,
                    Entra) ask for the base URL only and derive the Users/Groups paths automatically.
                </Typography>
                <Stack spacing={2} sx={{ mb: 4 }}>
                    <CopyableField
                        label="SCIM base URL"
                        value={endpoints?.baseUrl}
                        snackbarMessage="SCIM base URL copied"
                    />
                    <CopyableField
                        label="Users endpoint"
                        value={endpoints?.usersUrl}
                        snackbarMessage="Users endpoint copied"
                    />
                    <CopyableField
                        label="Groups endpoint"
                        value={endpoints?.groupsUrl}
                        snackbarMessage="Groups endpoint copied"
                    />
                </Stack>
                <Divider sx={{ mb: 4 }} />
            </If>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Bearer tokens</Typography>
                <Button variant="contained" onClick={createToken} data-cy="scim-create-token-button">
                    Create token
                </Button>
            </Stack>

            <If value={tokens.length > 0}>
                <Stack spacing={1}>
                    <Repeat list={tokens} item={<TokenRow />} keyFn={keyFn} pass="token" />
                </Stack>
                <Else>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No SCIM tokens yet. Create one to connect your IdP.
                    </Typography>
                </Else>
            </If>

            <Divider sx={{ my: 4 }} />

            <CircuitBreakerSection />
        </Box>
    )

    function keyFn(token) {
        return token._id
    }

    async function createToken() {
        await handleCreateToken({ clientId, scimBaseUrl: endpoints?.baseUrl })
    }
}
